import { useCallback, useLayoutEffect, useState } from 'react';

import { IMAGE_MODAL_V2_BOTTOM_SURFACE, type ImageModalV2BottomSurface } from './imageModalV2BottomSurface';

type BottomSurfacePresentation = {
    active: ImageModalV2BottomSurface;
    exiting: ImageModalV2BottomSurface | null;
    pending: ImageModalV2BottomSurface | null;
};

/** Whether a surface is the primary toolbar, as opposed to any of the other bottom-band surfaces. */
const isPrimary = (surface: ImageModalV2BottomSurface): boolean =>
    surface === IMAGE_MODAL_V2_BOTTOM_SURFACE.PRIMARY_TOOLBAR;

/**
 * The primary toolbar and every other surface occupy visually distinct positions (the toolbar sits at
 * the very bottom; everything else sits in the band just above it), so swapping between "primary" and
 * "non-primary" can run as two independent, simultaneous motions instead of a sequential exit-then-enter.
 */
const isSimultaneousTransition = (from: ImageModalV2BottomSurface, to: ImageModalV2BottomSurface): boolean =>
    isPrimary(from) !== isPrimary(to);

/**
 * Advances the presentation state machine toward `desired`.
 *
 * - If `desired` is already active (and nothing is exiting/pending), nothing changes.
 * - If `desired` is the surface currently exiting, it is reinstated as active (the exit is cancelled).
 * - If a transition between the same "axis" (primary vs non-primary, see `isSimultaneousTransition`) is
 *   already in flight, `desired` replaces whichever slot (exiting/pending) is appropriate for that axis.
 * - Otherwise the active surface starts exiting and `desired` becomes active immediately (simultaneous
 *   transition) or is queued as `pending` until the current exit completes (sequential transition).
 */
const requestSurface = (
    state: BottomSurfacePresentation,
    desired: ImageModalV2BottomSurface,
): BottomSurfacePresentation => {
    if (desired === state.active && !state.exiting && !state.pending) return state;
    if (desired === state.active) return { active: desired, exiting: null, pending: null };
    if (desired === state.exiting) {
        return { active: desired, exiting: state.active === desired ? null : state.active, pending: null };
    }

    if (state.exiting && state.pending) {
        return isSimultaneousTransition(state.exiting, desired)
            ? { active: desired, exiting: state.exiting, pending: null }
            : { ...state, pending: desired };
    }

    if (state.exiting) {
        return isSimultaneousTransition(state.active, desired)
            ? { active: desired, exiting: state.active, pending: null }
            : { active: state.active, exiting: state.active, pending: desired };
    }

    return isSimultaneousTransition(state.active, desired)
        ? { active: desired, exiting: state.active, pending: null }
        : { active: state.active, exiting: state.active, pending: desired };
};

/**
 * Marks `exited`'s exit animation as finished. If another surface was queued behind it (`pending`), it
 * becomes active immediately (its own enter animation is driven separately, by the caller re-mounting
 * or re-styling it once `getSurfaceState` reports it open). Ignored if `exited` is not the surface
 * currently exiting (e.g. a late-firing transition callback for a surface that has already resolved).
 */
const completeSurfaceExit = (
    state: BottomSurfacePresentation,
    exited: ImageModalV2BottomSurface,
): BottomSurfacePresentation => {
    if (state.exiting !== exited) return state;
    if (state.pending) return { active: state.pending, exiting: null, pending: null };
    return { ...state, exiting: null };
};

/**
 * Drives which single bottom-band surface (of the image modal's toolbar/file-transfer/modal-operation
 * surfaces) is presented at a time, sequencing transitions so an outgoing surface finishes exiting
 * before an incoming one enters — unless the two sit on different visual "axes" (see
 * `isSimultaneousTransition`), in which case they may animate at once.
 *
 * The state machine tracks three slots: `active` (currently shown/showing), `exiting` (currently
 * animating out), and `pending` (queued to become active once `exiting` finishes). Callers drive it by
 * calling this hook with the currently-desired surface every render, reading per-surface open/mount
 * state via `getSurfaceState`, and calling `completeExit` once a surface's own exit animation ends.
 *
 * @param desired The surface that should be shown given the modal's current state (e.g. mode, whether
 * a file transfer or modal operation is in progress).
 * @returns `openSurface` — the surface that should currently be treated as "open" for layout purposes
 * (`null` during a sequential exit-then-enter handoff, since nothing should be treated as open then);
 * `completeExit` — call with a surface once its exit transition has finished;
 * `getSurfaceState` — given a surface, returns whether it `isOpen` (should be actively shown) and
 * whether it `shouldMount` (is open, or still exiting and therefore must stay mounted).
 */
export const useImageModalV2BottomSurfacePresentation = (desired: ImageModalV2BottomSurface) => {
    const [presentation, setPresentation] = useState<BottomSurfacePresentation>({
        active: desired,
        exiting: null,
        pending: null,
    });

    useLayoutEffect(() => {
        setPresentation((current) => requestSurface(current, desired));
    }, [desired]);

    const completeExit = useCallback((surface: ImageModalV2BottomSurface) => {
        setPresentation((current) => completeSurfaceExit(current, surface));
    }, []);

    const getSurfaceState = useCallback(
        (surface: ImageModalV2BottomSurface) => {
            const isSequentialExit = presentation.exiting === surface && presentation.pending !== null;
            const isOpen = presentation.active === surface && !isSequentialExit;
            return { isOpen, shouldMount: isOpen || presentation.exiting === surface };
        },
        [presentation],
    );

    const openSurface =
        presentation.exiting === presentation.active && presentation.pending ? null : presentation.active;

    return { openSurface, completeExit, getSurfaceState };
};
